<!-- TODO: rename component, name clash with standard html component "textarea" -->
<script setup lang="ts">
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
import { useField } from 'vee-validate'
import { type Component, computed, nextTick, ref, useAttrs, useTemplateRef, watch } from 'vue'

import IconSparkles from '@/components/icons/IconSparkles.vue'

import TooltipApplier from '../overlays/TooltipApplier.vue'

const props = withDefaults(
  defineProps<{
    capitalizeFirst?: boolean
    disabled?: boolean
    errorMessage?: string
    inCreateContext?: boolean
    isFilledByAi?: boolean
    isPassword?: boolean
    label?: string
    name?: string
    placeholder?: string
    rows?: number
    status?: 'error' | 'success' | undefined
  }>(),
  {
    capitalizeFirst: true,
  },
)

// eslint-disable-next-line vue/define-emits-declaration
const emit = defineEmits<(e: 'manual-input') => void>()

const growWrapRef = useTemplateRef<HTMLDivElement>('growWrapRef')

defineSlots<{ icon: Component }>()
defineOptions({ inheritAttrs: false })

const inputEl = ref<HTMLTextAreaElement | null>(null)

const {
  errorMessage: vErrorMessage,
  meta,
  value: vValue,
} = useField<string>(() => props.name ?? '', undefined, { validateOnValueUpdate: false })

const model = defineModel<string>({
  default: '',
  // eslint-disable-next-line no-nested-ternary
  get: (value) => (value ? value : props.name ? vValue.value : value),
  set: (value) => {
    if (props.name) vValue.value = value
    return value
  },
})

watch(
  () => vValue.value,
  (newValue) => {
    model.value = newValue
  },
)

const displayError = computed(() => props.errorMessage ?? (meta.dirty ? vErrorMessage.value : ''))
const computedStatus = computed<typeof props.status>(() =>
  displayError.value ? 'error' : props.status,
)

const focusInput = (): void => {
  if (inputEl.value) inputEl.value.focus()
}

const attributesUse = useAttrs()
const { class: classAttr, ...attrs } = attributesUse

const inputTextClass = computed(() => (props.isFilledByAi ? 'text-lochmara-600 italic' : ''))

const onInput = async (event: Event): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const target = event.target as HTMLTextAreaElement
  const { value } = target
  if (props.name) vValue.value = value
  else model.value = value

  // eslint-disable-next-line vue/custom-event-name-casing
  emit('manual-input')
  await nextTick(() => {
    if (props.capitalizeFirst && typeof value === 'string' && value.length > 0) {
      const capitalized = value.charAt(0).toUpperCase() + value.slice(1)

      if (props.name) vValue.value = capitalized
      else model.value = capitalized

      if (inputEl.value) inputEl.value.value = capitalized
    }
  })
}
</script>

<template>
  <div
    class="relative rounded-lg border border-black-200 bg-white pb-3 pl-5 pt-8 text-sm"
    :class="[
      classAttr,
      !!displayError || !!isPassword ? 'pr-9' : 'pr-5',
      computedStatus === 'error' ? 'border-radical_red-500' : '',
      computedStatus === 'success' ? 'border-keppel-500' : '',
      disabled
        ? 'cursor-not-allowed border-black-100 bg-black-100'
        : computedStatus === undefined
          ? 'cursor-text hover:border-lochmara-600'
          : 'cursor-text',
    ]"
    @click="focusInput"
  >
    <div ref="growWrapRef" class="grow-wrap break-all text-sm" :data-replicated-value="model">
      <IconSparkles
        v-if="isFilledByAi"
        class="absolute bottom-[12px] right-[8px] size-5 text-lochmara-600"
      />
      <textarea
        ref="inputEl"
        v-bind="attrs"
        class="w-full min-w-0 max-w-full resize-none border-none bg-[inherit] p-0 placeholder:text-black-400 focus:ring-0"
        :class="[
          disabled ? 'cursor-not-allowed text-black-400' : 'cursor-text text-black-900',
          inputTextClass,
        ]"
        :disabled="!!disabled"
        :placeholder="placeholder || ''"
        :rows="rows ?? 3"
        :value="model"
        @input="onInput"
      ></textarea>
    </div>

    <div
      class="absolute right-5 top-3 h-3 w-3 cursor-pointer text-black-400 empty:pointer-events-none"
    >
      <slot name="icon"></slot>
    </div>

    <div class="absolute bottom-3 right-5">
      <TooltipApplier
        v-if="!!displayError"
        :align="inCreateContext ? 'right' : 'center'"
        is-red
        :label="displayError"
      >
        <ExclamationCircleIcon class="h-4 w-4 text-radical_red-500" />
      </TooltipApplier>
    </div>

    <label
      v-if="label"
      class="pointer-events-none absolute left-5 top-3 text-xs font-medium text-black-400"
      :class="[computedStatus === 'error' ? 'text-radical_red-500' : '']"
      >{{ label }}
    </label>
  </div>
</template>

<style scoped>
.grow-wrap {
  display: grid;
}
.grow-wrap::after {
  content: attr(data-replicated-value) ' ';
  white-space: pre-wrap;
  visibility: hidden;
}
.grow-wrap > textarea {
  resize: none;
  overflow: hidden;
}
.grow-wrap > textarea,
.grow-wrap::after {
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  grid-area: 1 / 1 / 2 / 2;
}
</style>
